'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';

import { logError } from 'commons/logger';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import ProductListingPagePlaceholder from 'components/consumer/ProductListingPage/ProductListingPagePlaceholder';
import PDPPlaceholder from 'components/consumer/PDP/PDPPlaceholder';
 
const DynamicAssociatePage = dynamic(
  () =>
    import(
      'components/consumer/associate'
    )
);

const DynamicBanner = dynamic(
  () =>
    import(
      'components/consumer/Banner'
    )
);

const DynamicBlog = dynamic(
  () =>
    import(
      'components/consumer/blog'
    )
);

const DynamicBuildUpdatesPage = dynamic(
  () =>
    import(
      'components/consumer/user-orders-build-updates'
    )
);

const DynamicCartPage = dynamic(
  () =>
    import(
      'components/consumer/cart-page'
    )
);

const DynamicCategoriesNavigation = dynamic(
  () =>
    import(
      'components/consumer/categories-navigation'
    )
);

const DynamicCategoriesNavigation2 = dynamic(
  () =>
    import(
      'components/consumer/CategoriesNavigation2'
    )
);

const DynamicCategoryDynPage = dynamic(
  () =>
    import(
      'components/consumer/CategoryPage'
    ), { loading: ProductListingPagePlaceholder }
);

const DynamicCollectionPage = dynamic(
  () =>
    import(
      'components/consumer/collection-page'
    )
);

const DynamicCollectionsPage = dynamic(
  () =>
    import(
      'components/consumer/collections-page'
    )
);

const DynamicConsumerAdvancedSearch = dynamic(
  () =>
    import(
      'components/consumer/consumer-search/advanced-search'
    ), { loading: ProductListingPagePlaceholder }
);

const DynamicConsumerAdvancedSearchRedirect = dynamic(
  () =>
    import(
      'components/consumer/consumer-search/advanced-search-redirect'
    )
);

const DynamicConsumerBlogSlider = dynamic(
  () =>
    import(
      'components/consumer/ConsumerBlogSlider'
    )
);

const DynamicConsumerBreadcrumb = dynamic(
  () =>
    import(
      'components/consumer/consumer-breadcrumb'
    )
);

const DynamicConsumerBusinessTradeForm = dynamic(
  () =>
    import(
      'components/consumer/consumer-business-trade-form'
    )
);

const DynamicConsumerClearancePage = dynamic(
  () =>
    import(
      'components/consumer/ConsumerClearancePage'
    )
);

const DynamicConsumerCollectionsSlider = dynamic(
  () =>
    import(
      'components/consumer/ConsumerCollectionsSlider'
    ), { loading: () => <div className="min-h-[350px]" /> }
);

const DynamicConsumerFlashsalePage = dynamic(
  () =>
    import(
      'components/consumer/ConsumerFlashsalePage'
    )
);

const DynamicConsumerLoginMobile = dynamic(
  () =>
    import(
      'components/consumer/consumer-login/login-page'
    )
);

const DynamicConsumerQuickShipPage = dynamic(
  () =>
    import(
      'components/consumer/ConsumerQuickshipPage'
    )
);

const DynamicConsumerSignupMobile = dynamic(
  () =>
    import(
      'components/consumer/consumer-login/signup-page'
    )
);

const DynamicContactEnquiryForm = dynamic(
  () =>
    import(
      'components/consumer/ContactEnquiryForm'
    )
);

const DynamicContentfulPromotionHero = dynamic(
  () =>
    import(
      'components/consumer/contentful-promotion-hero'
    ), { loading: () => <div className="lg:h-[560px] h-[50vw]" /> }
);

const DynamicCouponsSubscribeForm = dynamic(
  () =>
    import(
      'components/consumer/coupons-subscribe-form'
    )
);

const DynamicCustomProductList = dynamic(
  () =>
    import(
      'components/consumer/CustomProductList'
    )
);

const DynamicCustomerPressArticles = dynamic(
  () =>
    import(
      'components/consumer/consumer-press-articles'
    )
);

const DynamicCustomerPressSlider = dynamic(
  () =>
    import(
      'components/consumer/consumer-press-slider'
    )
);

const DynamicCustomerReviewsListByProduct = dynamic(
  () =>
    import(
      'components/consumer/customer-reviews-list-by-product'
    )
);

const DynamicCustomerReviewsPage = dynamic(
  () =>
    import(
      'components/consumer/customer-reviews-page'
    )
);

const DynamicDSRForm = dynamic(
  () =>
    import(
      'components/consumer/dsr-form'
    )
);

const DynamicDSRetailEmailOptin = dynamic(
  () =>
    import(
      'components/consumer/DSRetailEmailOptin'
    )
);

const DynamicDesignServicesFollowUpQuiz = dynamic(
  () =>
    import(
      'components/consumer/DesignServicesFollowUpQuiz'
    )
);

const DynamicDesignServicesInStoreQuiz = dynamic(
  () =>
    import(
      'components/consumer/DesignServicesInStoreQuiz'
    )
);

const DynamicDesignServicesQuiz = dynamic(
  () =>
    import(
      'components/consumer/DesignServicesQuiz'
    )
);

const DynamicDesktopCheckout = dynamic(
  () =>
    import(
      'components/consumer/checkout/desktop'
    )
);

const DynamicDualVideoSet = dynamic(
  () =>
    import(
      'components/consumer/dual-video-set'
    )
);

const DynamicDynamicShowroomDetails = dynamic(
  () =>
    import(
      'components/consumer/DynamicShowroomDetails'
    )
);

const DynamicDynamicShowroomProductList = dynamic(
  () =>
    import(
      'components/consumer/DynamicShowroomProductList'
    )
);

const DynamicDynamicShowroomsList = dynamic(
  () =>
    import(
      'components/consumer/DynamicShowroomsList'
    )
);

const DynamicFabricFilter = dynamic(
  () =>
    import(
      'components/consumer/fabric-filter'
    )
);

const DynamicFabricGrid = dynamic(
  () =>
    import(
      'components/consumer/fabric-grid'
    )
);

const DynamicFavoritesList = dynamic(
  () =>
    import(
      'components/consumer/favorites-list'
    )
);

const DynamicFeaturedIn = dynamic(
  () =>
    import(
      'components/consumer/consumer-home-page/FeaturedIn'
    ), { loading: () => <div className="min-h-[120px]" /> }
);

const DynamicFlatColorBanner = dynamic(
  () =>
    import(
      'components/consumer/flat-color-banner'
    )
);

const DynamicFluidTextBlocks2 = dynamic(
  () =>
    import(
      'components/consumer/fluid-text-blocks2'
    )
);

const DynamicFluidTextBlocks3 = dynamic(
  () =>
    import(
      'components/consumer/fluid-text-blocks3'
    )
);

const DynamicHalfImageHalfText = dynamic(
  () =>
    import(
      'components/consumer/half-image-half-text'
    )
);

const DynamicHorizontalSpacer = dynamic(
  () =>
    import(
      'components/consumer/horizontal-spacer'
    )
);

const DynamicHtmlEmbed = dynamic(
  () =>
    import(
      'components/consumer/HtmlEmbed'
    )
);

const DynamicIconTextSet = dynamic(
  () =>
    import(
      'components/consumer/icon-text-set'
    )
);

const DynamicImageAndContent = dynamic(
  () =>
    import(
      'components/consumer/ImageAndContent'
    )
);

const DynamicImageBanner = dynamic(
  () =>
    import(
      'components/consumer/image-banner'
    ), { loading: () => <div className="aspect-[2/1] lg:aspect-[3/1]" /> }
);

const DynamicImageBlocksThree = dynamic(
  () =>
    import(
      'components/consumer/image-blocks-three'
    )
);

const DynamicImageBlocksTwo = dynamic(
  () =>
    import(
      'components/consumer/image-blocks-two'
    )
);

const DynamicImageHalfTextLeft = dynamic(
  () =>
    import(
      'components/consumer/media/desktop/Image-Half-Text-Left'
    )
);

const DynamicImageSlider = dynamic(
  () =>
    import(
      'components/consumer/image-slider'
    ), { loading: () => <div className="min-h-[400px] max-[767px]:min-h-[300px]" /> }
);

const DynamicImageSliderStandard = dynamic(
  () =>
    import(
      'components/consumer/image-slider-standard'
    ), { loading: () => <div className="min-h-[400px] max-[767px]:min-h-[300px]" /> }
);

const DynamicImageWithTextBanner2 = dynamic(
  () =>
    import(
      'components/consumer/image-with-text-banner2'
    ), { loading: () => <div className="lg:h-[560px] h-[50vw]" /> }
);

const DynamicInspirationPhotoGrid = dynamic(
  () =>
    import(
      'components/consumer/inspiration-photos-grid'
    )
);

const DynamicKeySellingPointsBar = dynamic(
  () =>
    import(
      'components/consumer/KeySellingPointsBar'
    ), { loading: () => <div className="h-[62px] md:h-20" /> }
);

const DynamicLivestreamPage = dynamic(
  () =>
    import(
      'components/consumer/LivestreamPage'
    )
);

const DynamicModularConfigurator = dynamic(
  () =>
    import(
      'components/consumer/modular-configurator'
    )
);

const DynamicModularSofasPage = dynamic(
  () =>
    import(
      'components/consumer/modular-sofas-page'
    )
);

const DynamicMultipleBlocks = dynamic(
  () =>
    import(
      'components/consumer/MultipleBlocks'
    )
);

const DynamicOrderMaterialSelector = dynamic(
  () =>
    import(
      'components/consumer/user-order-materials'
    )
);

const DynamicOrderThankYou = dynamic(
  () =>
    import(
      'components/consumer/checkout/desktop/components/OrderThankYou'
    )
);

const DynamicOrderTrackingPage = dynamic(
  () =>
    import(
      'components/consumer/order-tracking-page'
    )
);

const DynamicPDPv2 = dynamic(
  () =>
    import(
      'components/consumer/PDP'
    )
);

const DynamicPDPv2Redirect = dynamic(
  () =>
    import(
      'components/consumer/PDPv2Redirect'
    )
);

const DynamicProductAndCategory = dynamic(
  () =>
    import(
      'components/consumer/product-and-category'
    ), { loading: PDPPlaceholder }
);

const DynamicProductsByMaterial = dynamic(
  () =>
    import(
      'components/consumer/products-by-material'
    )
);

const DynamicProjectSimonPage = dynamic(
  () =>
    import(
      'components/consumer/ProjectSimonPage'
    )
);

const DynamicRecentlyViewed = dynamic(
  () =>
    import(
      'components/consumer/recently-viewed'
    )
);

const DynamicSalesBanner = dynamic(
  () =>
    import(
      'components/consumer/sales-banner'
    )
);

const DynamicShopByStyle = dynamic(
  () =>
    import(
      'components/consumer/shop-by-style'
    )
);

const DynamicShopPageColumns = dynamic(
  () =>
    import(
      'components/consumer/ShopPageColumns'
    )
);

const DynamicSingleHeroVideo = dynamic(
  () =>
    import(
      'components/consumer/SingleHeroVideo'
    )
);

const DynamicSitemap = dynamic(
  () =>
    import(
      'components/consumer/sitemap'
    )
);

const DynamicSpecialCategoryWrapper = dynamic(
  () =>
    import(
      'components/consumer/SpecialCategoryWrapper'
    )
);

const DynamicStoreLocatorPage = dynamic(
  () =>
    import(
      'components/consumer/StoreLocatorPage'
    )
);

const DynamicSubmitPhotos = dynamic(
  () =>
    import(
      'components/consumer/submit-photos'
    )
);

const DynamicSwatchRequestForm = dynamic(
  () =>
    import(
      'components/consumer/swatch-request-form'
    )
);

const DynamicTestimonials = dynamic(
  () =>
    import(
      'components/consumer/consumer-testimonials'
    ), { loading: () => <div className="min-h-[300px]" /> }
);

const DynamicTextComponent = dynamic(
  () =>
    import(
      'components/consumer/media/desktop/Text-Component'
    )
);

const DynamicTextComponentNew = dynamic(
  () =>
    import(
      'components/consumer/text-component'
    )
);

const DynamicTextContent = dynamic(
  () =>
    import(
      'components/consumer/TextContent'
    )
);

const DynamicTrackOrderStatus = dynamic(
  () =>
    import(
      'components/consumer/TrackOrderStatus'
    )
);

const DynamicTwoBoxes = dynamic(
  () =>
    import(
      'components/consumer/TwoBoxes'
    )
);

const DynamicTwoSquares = dynamic(
  () =>
    import(
      'components/consumer/TwoSquares'
    )
);

const DynamicUpdateAccountInfo = dynamic(
  () =>
    import(
      'components/consumer/user-account/update-info'
    )
);

const DynamicUserCreateQuote = dynamic(
  () =>
    import(
      'components/consumer/user-quotes-page/create-quote'
    )
);

const DynamicUserDashboard = dynamic(
  () =>
    import(
      'components/consumer/user-dashboard'
    )
);

const DynamicUserNotificationSettingsPage = dynamic(
  () =>
    import(
      'components/consumer/user-notification-settings'
    )
);

const DynamicUserOrdersInfo = dynamic(
  () =>
    import(
      'components/consumer/user-orders-info'
    )
);

const DynamicUserProtectionPlans = dynamic(
  () =>
    import(
      'components/consumer/user-protection-plans'
    )
);

const DynamicUserQuotesList = dynamic(
  () =>
    import(
      'components/consumer/user-quotes-page/quotes-list'
    )
);

const DynamicUserResetPassword = dynamic(
  () =>
    import(
      'components/consumer/consumer-login/reset-password'
    )
);

const DynamicUserReviewsPage = dynamic(
  () =>
    import(
      'components/consumer/user-reviews-page'
    )
);

const DynamicUserRewardsPage = dynamic(
  () =>
    import(
      'components/consumer/user-rewards-page'
    )
);

const DynamicUserSingleOrderInfo = dynamic(
  () =>
    import(
      'components/consumer/user-single-order-info'
    )
);

const DynamicUserViewQuote = dynamic(
  () =>
    import(
      'components/consumer/user-quotes-page/view-quote'
    )
);

const DynamicWarrantyForm = dynamic(
  () =>
    import(
      'components/consumer/WarrantyForm'
    )
);
 
const componentMapping = {
  'AssociatePage': DynamicAssociatePage,
  'Banner': DynamicBanner,
  'Blog': DynamicBlog,
  'BuildUpdatesPage': DynamicBuildUpdatesPage,
  'CartPage': DynamicCartPage,
  'CategoriesNavigation': DynamicCategoriesNavigation,
  'CategoriesNavigation2': DynamicCategoriesNavigation2,
  'CategoryDynPage': DynamicCategoryDynPage,
  'CollectionPage': DynamicCollectionPage,
  'CollectionsPage': DynamicCollectionsPage,
  'ConsumerAdvancedSearch': DynamicConsumerAdvancedSearch,
  'ConsumerAdvancedSearchRedirect': DynamicConsumerAdvancedSearchRedirect,
  'ConsumerBlogSlider': DynamicConsumerBlogSlider,
  'ConsumerBreadcrumb': DynamicConsumerBreadcrumb,
  'ConsumerBusinessTradeForm': DynamicConsumerBusinessTradeForm,
  'ConsumerClearancePage': DynamicConsumerClearancePage,
  'ConsumerCollectionsSlider': DynamicConsumerCollectionsSlider,
  'ConsumerFlashsalePage': DynamicConsumerFlashsalePage,
  'ConsumerLoginMobile': DynamicConsumerLoginMobile,
  'ConsumerQuickShipPage': DynamicConsumerQuickShipPage,
  'ConsumerSignupMobile': DynamicConsumerSignupMobile,
  'ContactEnquiryForm': DynamicContactEnquiryForm,
  'ContentfulPromotionHero': DynamicContentfulPromotionHero,
  'CouponsSubscribeForm': DynamicCouponsSubscribeForm,
  'CustomProductList': DynamicCustomProductList,
  'CustomerPressArticles': DynamicCustomerPressArticles,
  'CustomerPressSlider': DynamicCustomerPressSlider,
  'CustomerReviewsListByProduct': DynamicCustomerReviewsListByProduct,
  'CustomerReviewsPage': DynamicCustomerReviewsPage,
  'DSRForm': DynamicDSRForm,
  'DSRetailEmailOptin': DynamicDSRetailEmailOptin,
  'DesignServicesFollowUpQuiz': DynamicDesignServicesFollowUpQuiz,
  'DesignServicesInStoreQuiz': DynamicDesignServicesInStoreQuiz,
  'DesignServicesQuiz': DynamicDesignServicesQuiz,
  'DesktopCheckout': DynamicDesktopCheckout,
  'DualVideoSet': DynamicDualVideoSet,
  'DynamicShowroomDetails': DynamicDynamicShowroomDetails,
  'DynamicShowroomProductList': DynamicDynamicShowroomProductList,
  'DynamicShowroomsList': DynamicDynamicShowroomsList,
  'FabricFilter': DynamicFabricFilter,
  'FabricGrid': DynamicFabricGrid,
  'FavoritesList': DynamicFavoritesList,
  'FeaturedIn': DynamicFeaturedIn,
  'FlatColorBanner': DynamicFlatColorBanner,
  'FluidTextBlocks2': DynamicFluidTextBlocks2,
  'FluidTextBlocks3': DynamicFluidTextBlocks3,
  'HalfImageHalfText': DynamicHalfImageHalfText,
  'HorizontalSpacer': DynamicHorizontalSpacer,
  'HtmlEmbed': DynamicHtmlEmbed,
  'IconTextSet': DynamicIconTextSet,
  'ImageAndContent': DynamicImageAndContent,
  'ImageBanner': DynamicImageBanner,
  'ImageBlocksThree': DynamicImageBlocksThree,
  'ImageBlocksTwo': DynamicImageBlocksTwo,
  'ImageHalfTextLeft': DynamicImageHalfTextLeft,
  'ImageSlider': DynamicImageSlider,
  'ImageSliderStandard': DynamicImageSliderStandard,
  'ImageWithTextBanner2': DynamicImageWithTextBanner2,
  'InspirationPhotoGrid': DynamicInspirationPhotoGrid,
  'KeySellingPointsBar': DynamicKeySellingPointsBar,
  'LivestreamPage': DynamicLivestreamPage,
  'ModularConfigurator': DynamicModularConfigurator,
  'ModularSofasPage': DynamicModularSofasPage,
  'MultipleBlocks': DynamicMultipleBlocks,
  'OrderMaterialSelector': DynamicOrderMaterialSelector,
  'OrderThankYou': DynamicOrderThankYou,
  'OrderTrackingPage': DynamicOrderTrackingPage,
  'PDPv2': DynamicPDPv2,
  'PDPv2Redirect': DynamicPDPv2Redirect,
  'ProductAndCategory': DynamicProductAndCategory,
  'ProductsByMaterial': DynamicProductsByMaterial,
  'ProjectSimonPage': DynamicProjectSimonPage,
  'RecentlyViewed': DynamicRecentlyViewed,
  'SalesBanner': DynamicSalesBanner,
  'ShopByStyle': DynamicShopByStyle,
  'ShopPageColumns': DynamicShopPageColumns,
  'SingleHeroVideo': DynamicSingleHeroVideo,
  'Sitemap': DynamicSitemap,
  'SpecialCategoryWrapper': DynamicSpecialCategoryWrapper,
  'StoreLocatorPage': DynamicStoreLocatorPage,
  'SubmitPhotos': DynamicSubmitPhotos,
  'SwatchRequestForm': DynamicSwatchRequestForm,
  'Testimonials': DynamicTestimonials,
  'TextComponent': DynamicTextComponent,
  'TextComponentNew': DynamicTextComponentNew,
  'TextContent': DynamicTextContent,
  'TrackOrderStatus': DynamicTrackOrderStatus,
  'TwoBoxes': DynamicTwoBoxes,
  'TwoSquares': DynamicTwoSquares,
  'UpdateAccountInfo': DynamicUpdateAccountInfo,
  'UserCreateQuote': DynamicUserCreateQuote,
  'UserDashboard': DynamicUserDashboard,
  'UserNotificationSettingsPage': DynamicUserNotificationSettingsPage,
  'UserOrdersInfo': DynamicUserOrdersInfo,
  'UserProtectionPlans': DynamicUserProtectionPlans,
  'UserQuotesList': DynamicUserQuotesList,
  'UserResetPassword': DynamicUserResetPassword,
  'UserReviewsPage': DynamicUserReviewsPage,
  'UserRewardsPage': DynamicUserRewardsPage,
  'UserSingleOrderInfo': DynamicUserSingleOrderInfo,
  'UserViewQuote': DynamicUserViewQuote,
  'WarrantyForm': DynamicWarrantyForm
};

const ComponentFromJoystagram = ({ name, config }) => {
  const DynamicComponent = componentMapping[name];

  useEffect(() => {
    if (!DynamicComponent) {
      logError(
        new Error(`ComponentFromJoystagram: Component "${name}" not found`),
        { component: 'ComponentFromJoystagram', missingComponent: name }
      );
    }
  }, [DynamicComponent, name]);

  if (!DynamicComponent) {
    return null;
  }

  DynamicComponent.displayName = 'Dynamic' + name;

  return (
    <SuspenseWithLoader>
      <DynamicComponent config={config ? JSON.parse(config) : undefined} />
    </SuspenseWithLoader>
  );
};

export default ComponentFromJoystagram;
